/**
 * Helpers the ranker uses to apply version-gating decoration on the
 * Monaco-shaped output. The decoration metadata itself is set on the
 * candidate by `versionGating/decorate.ts`; this module only translates the
 * fields into the ranker's `MonacoCompletionItem` shape.
 *
 * Pure module — no Monaco runtime dep.
 */

import type { CompletionCandidate } from '@/core/engines';

export type VersionDecorationFields = {
  readonly versionStatus: 'supported' | 'unavailable' | 'deprecated';
  readonly badge: string;
  readonly detailSuffix: string;
  readonly demote: 0 | 1 | 2;
};

export function decoratedDemote(c: CompletionCandidate): 0 | 1 | 2 {
  const d = (c as Partial<VersionDecorationFields>).demote;
  return d === 1 || d === 2 ? d : 0;
}

export function decoratedBadge(c: CompletionCandidate): string | undefined {
  const b = (c as Partial<VersionDecorationFields>).badge;
  return typeof b === 'string' && b.length > 0 ? b : undefined;
}

export function decoratedDetailSuffix(c: CompletionCandidate): string | undefined {
  const s = (c as Partial<VersionDecorationFields>).detailSuffix;
  return typeof s === 'string' && s.length > 0 ? s : undefined;
}

export function isDeprecated(c: CompletionCandidate): boolean {
  return (c as Partial<VersionDecorationFields>).versionStatus === 'deprecated';
}

export function mergeDetail(detail: string | undefined, suffix: string | undefined): string | undefined {
  if (!suffix) return detail;
  if (!detail) return suffix;
  return `${detail} — ${suffix}`;
}

export function clampTier(t: number): number {
  if (t < 1) return 1;
  if (t > 8) return 8;
  return t;
}
